import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  neighborWatchAdd,
  neighborWatchCheck,
  neighborWatchQuery,
  neighborWatchRemove,
} from '../api/devices'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'
import './DeviceNeighborWatch.css'

/**
 * Fila Neighbor Watch - proactivitee/devices/details/neighborWatch.php:
 * Up / Down / Unknown, select + Watch / Check now, tabelul de masini.
 */

export function DeviceNeighborWatch({ id }: ScreenProps) {
  const page = useQuery(neighborWatchQuery(id))
  const [target, setTarget] = useState('')
  const [checking, setChecking] = useState(false)
  const [busy, setBusy] = useState(false)

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const data = page.data
  const reload = () => page.refetch()

  return (
    <div className="device-neighbor-watch">
      <div className="nw-board">
        <div className="nw-card up">
          <div className="k">Up</div>
          <div className="v">{data.counts.up}</div>
        </div>
        <div className="nw-card down">
          <div className="k">Down</div>
          <div className="v">{data.counts.down}</div>
        </div>
        <div className="nw-card unknown">
          <div className="k">Unknown</div>
          <div className="v">{data.counts.unknown}</div>
        </div>
      </div>

      <div className="nw-section">
        <div className="nw-toolbar">
          <select id="nwCandidate" className="form-control" value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">Add a registered device…</option>
            {data.candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} — {c.managed ? 'Managed' : 'Unmanaged'}
                {c.ip !== '' ? ` · ${c.ip}` : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-theme"
            id="nwAdd"
            disabled={busy}
            onClick={() => {
              const chosen = parseInt(target, 10) || 0
              if (!chosen) return
              setBusy(true)
              void neighborWatchAdd(id, chosen)
                .then((res) => {
                  if (res.ok) {
                    setTarget('')
                    return reload()
                  }
                  window.alert(res.error || 'Could not add the device.')
                })
                .catch(() => window.alert('Could not add the device.'))
                .finally(() => setBusy(false))
            }}
          >
            <i className="fas fa-plus"></i> Watch
          </button>
          <button
            type="button"
            className="btn btn-default"
            id="nwCheck"
            disabled={checking}
            onClick={() => {
              setChecking(true)
              void neighborWatchCheck(id)
                .then((res) => {
                  if (res.ok) {
                    window.setTimeout(() => {
                      void reload().finally(() => setChecking(false))
                    }, 4000)
                    return
                  }
                  window.alert(res.error || 'Could not start the check.')
                  setChecking(false)
                })
                .catch(() => {
                  window.alert('Could not start the check.')
                  setChecking(false)
                })
            }}
          >
            <i className="fas fa-sync"></i> Check now
          </button>
        </div>

        {data.watches.length === 0 ? (
          <div className="nw-empty">
            No devices yet. Pick a Managed or Unmanaged device already in the inventory. This machine will ping it from
            inside the LAN.
          </div>
        ) : (
          <table className="nw-table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Kind</th>
                <th>Address</th>
                <th>Status</th>
                <th>RTT</th>
                <th>Checked</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.watches.map((w) => (
                <tr key={w.id} data-watch={w.id}>
                  <td>
                    <strong>{w.name}</strong>
                    {w.type_name !== '' && <div className="nw-kind">{w.type_name}</div>}
                  </td>
                  <td>{w.managed ? 'Managed' : 'Unmanaged'}</td>
                  <td>{w.ip !== '' ? w.ip : '—'}</td>
                  <td>
                    <span className={`nw-pill ${w.status}`}>{w.status.toUpperCase()}</span>
                  </td>
                  <td>{w.rtt_ms !== null ? `${w.rtt_ms} ms` : '—'}</td>
                  <td>{w.last_checked ? w.last_checked : '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-xs btn-danger nw-remove"
                      title="Stop watching"
                      onClick={() => {
                        void neighborWatchRemove(id, w.id)
                          .then(() => reload())
                          .catch(() => window.alert('Could not remove the device.'))
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
